import { fetchFromLambda } from '@agyletime/vue-3-common/src/utils/apiService';
import moment from 'moment-timezone';

type EventLogReturn = {
    eventType: string;
    eventDate: string;
    message: string;
    service: string;
    eventLevel: string;
    eventSpecificData: unknown;
    createdAt: string;
    updatedAt: string;
};

type EventLogItem = {
    eventType: string;
    eventDate: string;
    eventMessage: string;
    eventService: string;
    eventLevel: string;
    eventSpecificData: unknown;
};
type EventLogReturnStructure = {
    data: EventLogReturn[];
    totalCount: number;
}
type EventLogStructure = {
    data: EventLogItem[];
    totalCount: number;
}
const firstLetterToUppercase = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
};
type GetEventLogsParams = {
    eventTypes: string[];
    eventLevels: string[];
    eventServices: string[];
    timezone: string;
    startDate: string;
    endDate: string;
};
export const getEventLogs = async ({
    eventTypes,
    eventLevels,
    eventServices,
    timezone,
    startDate,
    endDate
}: GetEventLogsParams): Promise<EventLogStructure> => {
    const eventTypesQuery: string | boolean = createEventTypesQuery(eventTypes);
    const eventLevelsQuery: string | boolean = createEventLevelsQuery(eventLevels);
    const eventServicesQuery: string | boolean = createEventServicesQuery(eventServices);
    const datesQuery: string | boolean = createDatesQuery(startDate, endDate, timezone);
    const eventLogUrl = generateEventLogsUrl(eventTypesQuery, eventLevelsQuery, eventServicesQuery, datesQuery, 0, 5000);
    const eventLogResults: EventLogReturnStructure[] = await fetchFromLambda(eventLogUrl);
    const totalEventLogs: EventLogItem[] = [];
    if (!eventLogResults || !eventLogResults.length) {
        return {
            data: [],
            totalCount: 0
        };
    }
    const mappedEventLogs = mapEventLogs(eventLogResults[0].data);
    const totalCount = eventLogResults[0].totalCount;
    totalEventLogs.push(...mappedEventLogs);
    while (totalEventLogs.length < totalCount) {
        const newStartRow = totalEventLogs.length;
        const newEventLogUrl = generateEventLogsUrl(eventTypesQuery, eventLevelsQuery, eventServicesQuery, datesQuery, newStartRow, 5000);
        const newEventLogResults: EventLogReturnStructure[] = await fetchFromLambda(newEventLogUrl);
        const newMappedEventLogs = mapEventLogs(newEventLogResults[0].data);
        totalEventLogs.push(...newMappedEventLogs);
    }
    return {
        data: totalEventLogs,
        totalCount: totalCount
    };
};

const mapEventLogs = (eventLogs: EventLogReturn[]): EventLogItem[] => {
    const mappedEvents = eventLogs.map((elem) => {
        return {
            eventType: elem.eventType,
            eventDate: new Date(elem.updatedAt).toLocaleDateString(undefined, {
                month: '2-digit',
                day: '2-digit',
                year: undefined,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            }),
            eventMessage: elem.message,
            eventService: elem.service ? elem.service : 'Workactivity',
            eventLevel: firstLetterToUppercase(elem.eventLevel),
            eventSpecificData: elem.eventSpecificData
        } as EventLogItem;
    });
    return mappedEvents;
};

const createEventTypesQuery = (eventTypes: string[]) => {
    if (!eventTypes || !eventTypes.length) return false;
    let eventTypesQuery = '';
    for (const eventType of eventTypes) {
        eventTypesQuery += `${eventType},`;
    }
    eventTypesQuery = eventTypesQuery.slice(0, -1);
    return eventTypesQuery;
};

const createEventLevelsQuery = (eventLevels: string[]) => {
    if (!eventLevels || !eventLevels.length) return false;
    let eventLevelsQuery = '';
    for (const eventLevel of eventLevels) {
        eventLevelsQuery += `${eventLevel},`;
    }
    eventLevelsQuery = eventLevelsQuery.slice(0, -1);
    return eventLevelsQuery;
};

const createEventServicesQuery = (eventServices: string[]) => {
    if (!eventServices || !eventServices.length) return false;
    let eventServicesQuery = '';
    for (const eventService of eventServices) {
        eventServicesQuery += `${eventService},`;
    }
    eventServicesQuery = eventServicesQuery.slice(0, -1);
    return eventServicesQuery;
};

// eslint-disable-next-line complexity
const createDatesQuery = (startDate: string, endDate: string, timezone: string ) => {
    if (!startDate || !endDate) return false;
    let dateQuery = '';
    if (startDate && endDate) {
        const startTime = moment(startDate).tz(moment.tz.guess()).tz(timezone ? timezone : moment.tz.guess(), true).toISOString();
        const endTime = moment(endDate).tz(moment.tz.guess()).tz(timezone ? timezone : moment.tz.guess(), true).toISOString();
        const startDateFilter = `&startTime=${startTime}&endTime=${endTime}`;
        dateQuery = startDateFilter;
    }
    return dateQuery;
};

// eslint-disable-next-line complexity
const generateEventLogsUrl = (
    eventTypesQuery: string | boolean,
    eventLevelsQuery: string | boolean,
    eventServicesQuery: string | boolean,
    datesQuery: string | boolean,
    startRow: number | undefined,
    pageSize: number | undefined
    // eslint-disable-next-line max-params
) => {
    let baseUrl = '/event-logs/events/?';
    if (eventTypesQuery) {
        baseUrl += `eventTypes=${eventTypesQuery}&`;
    }
    if (eventLevelsQuery) {
        baseUrl += `eventLevels=${eventLevelsQuery}&`;
    }
    if (eventServicesQuery) {
        baseUrl += `services=${eventServicesQuery}&`;
    }
    if (datesQuery) {
        baseUrl += `${datesQuery}&`;
    }
    if (startRow !== undefined) {
        baseUrl += `startRow=${startRow}&`;
    }
    if (pageSize) {
        baseUrl += `pageSize=${pageSize}&`;
    }
    // Trim last character, no matter what
    baseUrl = baseUrl.slice(0, baseUrl.length - 1);
    return baseUrl;
};